import { useEffect, useRef } from 'react';
import { SessionManager } from '@/lib/sessionManager';

export const useScrollTracking = () => {
  const sessionManager = SessionManager.getInstance();
  const maxScrollRef = useRef(0);
  const lastUpdateRef = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;

      const scrollPercentage = Math.round(
        (scrollTop / (documentHeight - windowHeight)) * 100
      );

      if (scrollPercentage > maxScrollRef.current) {
        maxScrollRef.current = scrollPercentage;

        // Only update every 10% to avoid too many requests
        if (scrollPercentage >= lastUpdateRef.current + 10) {
          lastUpdateRef.current = scrollPercentage;
          sessionManager.updateScrollDepth(scrollPercentage);
        }
      }
    };

    const throttledScroll = throttle(handleScroll, 1000);
    window.addEventListener('scroll', throttledScroll);

    return () => window.removeEventListener('scroll', throttledScroll);
  }, []);
};

function throttle<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  let previous = 0;

  return function (this: any, ...args: Parameters<T>) {
    const now = Date.now();
    const remaining = wait - (now - previous);

    if (remaining <= 0 || remaining > wait) {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
      previous = now;
      func.apply(this, args);
    } else if (!timeout) {
      timeout = setTimeout(() => {
        previous = Date.now();
        timeout = null;
        func.apply(this, args);
      }, remaining);
    }
  };
}
